import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { doctorsApi } from '@/api';

const DOCTOR_SPECIALTIES_KEY = (doctorId: string) => ['doctor', doctorId, 'specialties'] as const;

export function useDoctorSpecialties(doctorId: string) {
  return useQuery({
    queryKey: DOCTOR_SPECIALTIES_KEY(doctorId),
    queryFn: () => doctorsApi.getMySpecialties(),
    staleTime: 0,
    gcTime: 1000 * 60 * 5,
  });
}

export function useAddDoctorSpecialty(doctorId: string) {
  const qc = useQueryClient();

  return useMutation({
    mutationFn: (data: { specialtyId: string; primary?: boolean }) =>
      doctorsApi.addMySpecialty(data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: DOCTOR_SPECIALTIES_KEY(doctorId) });
    },
  });
}

export function useRemoveDoctorSpecialty(doctorId: string) {
  const qc = useQueryClient();

  return useMutation({
    mutationFn: (specialtyId: string) =>
      doctorsApi.removeMySpecialty(specialtyId),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: DOCTOR_SPECIALTIES_KEY(doctorId) });
    },
  });
}

export function useSetDoctorPrimarySpecialty(doctorId: string) {
  const qc = useQueryClient();

  return useMutation({
    mutationFn: (specialtyId: string) =>
      doctorsApi.setMyPrimarySpecialty(specialtyId),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: DOCTOR_SPECIALTIES_KEY(doctorId) });
    },
  });
}

export function useReplaceDoctorSpecialties(doctorId: string) {
  const qc = useQueryClient();

  return useMutation({
    mutationFn: (data: { specialtyIds: string[]; primarySpecialtyId: string }) =>
      doctorsApi.replaceMySpecialties(data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: DOCTOR_SPECIALTIES_KEY(doctorId) });
    },
  });
}
